import { create } from 'zustand';
import type { Image, ImageCategory } from '../types/types';
import { api } from '../services/api';
import { logger } from '../services/logger.client';
import { immer } from 'zustand/middleware/immer';

interface ImageLibraryState {
  images: Image[];
}

interface ImageLibraryActions {
  setImages: (images: Image[]) => void;
  addImage: (name: string, category: string, imageUrl: string, imageCategories: ImageCategory[]) => Promise<void>;
  updateImage: (image: Partial<Image> & { id: number }) => Promise<void>;
  deleteImage: (id: number) => Promise<void>;
}

export const useImageLibraryStore = create<ImageLibraryState & ImageLibraryActions>()(immer((set) => {
    const actions: ImageLibraryActions = {
        setImages: (images) => set((state: ImageLibraryState) => { state.images = images; }),
        addImage: async (name, category, imageUrl, imageCategories) => {
            const categoryObj = (imageCategories ?? []).find((c) => c.name === category);
            if (!categoryObj) {
              logger.error({ category }, 'Category not found when adding image');
              return;
            }
            try {
              const response = await api('/api/images', { method: 'POST', body: JSON.stringify({ name, categoryId: categoryObj.id, imageUrl }) });
              const newImage = await response.json() as Image;
              set((state: ImageLibraryState) => { state.images.push(newImage); (state.images ?? []).sort((a: Image, b: Image) => a.name.localeCompare(b.name)); });
            } catch (error) {
              logger.error(error, 'Failed to save image to database');
            }
        },
        updateImage: async (imageUpdate) => {
            try {
              const response = await api(`/api/images/${imageUpdate.id}`, { method: 'PUT', body: JSON.stringify(imageUpdate) });
              const updatedImage = await response.json() as Image;
              set((state: ImageLibraryState) => {
                const index = (state.images ?? []).findIndex((img: Image) => img.id === imageUpdate.id);
                if (index !== -1) {
                  state.images[index] = { ...state.images[index]!, ...updatedImage };
                  (state.images ?? []).sort((a: Image, b: Image) => a.name.localeCompare(b.name));
                }
              });
            } catch (error) {
              logger.error(error, `Error updating image ${imageUpdate.id}`);
            }
        },
        deleteImage: async (id) => {
            try {
              await api(`/api/images/${id}`, { method: 'DELETE' });
              set((state: ImageLibraryState) => { state.images = (state.images ?? []).filter((img: Image) => img.id !== id); });
            } catch (error) {
              logger.error(error, `Error deleting image ${id}`);
            }
        }
    };

    return {
        images: [], // Initial state
        ...actions,
    };
}));